import { useRef, useState } from 'react';
import { api } from '../lib/api';
import { fmtTime } from '../lib/useAudio';

interface Result {
  videoId: string;
  title: string;
  artist: string;
  durationSec?: number;
}

export function TrackSearch() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Result[] | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [added, setAdded] = useState<string | null>(null);
  const seq = useRef(0);

  const go = async () => {
    const query = q.trim();
    if (!query) return;
    const my = ++seq.current;
    setState('loading');
    try {
      const r = await api.search(query);
      if (my !== seq.current) return;
      setResults(r.results.slice(0, 8));
      setState('idle');
    } catch {
      if (my !== seq.current) return;
      setResults(null);
      setState('error');
    }
  };

  const clear = () => {
    seq.current++;
    setQ('');
    setResults(null);
    setState('idle');
  };

  const add = (t: Result) => {
    api.queue([{ title: t.title, artist: t.artist }]).catch(() => {});
    setAdded(t.videoId);
    window.setTimeout(() => setAdded((cur) => (cur === t.videoId ? null : cur)), 1200);
  };

  return (
    <div className="mb-6">
      <div className="relative mt-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.key === 'Enter') go();
            else if (e.key === 'Escape') clear();
          }}
          enterKeyHint="search"
          placeholder="点歌 — 歌名、歌手…"
          className="w-full rounded-lg border bd surface px-3 py-2 pr-8 text-sm outline-none placeholder-faint focus:bd-strong"
        />
        {(q || results) && (
          <button
            aria-label="清空搜索"
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 px-1 text-faint hover:text-fg"
          >
            ×
          </button>
        )}
      </div>

      {state === 'loading' && <p className="py-3 text-center text-xs text-faint">搜索中…</p>}
      {state === 'error' && <p className="py-3 text-center text-xs text-faint">搜索失败了，稍后再试</p>}
      {state === 'idle' && results && results.length === 0 && (
        <p className="py-3 text-center text-xs text-faint">没搜到 — 换个关键词试试</p>
      )}
      {state === 'idle' && results && results.length > 0 && (
        <div>
          {results.map((t) => (
            <div
              key={t.videoId}
              onClick={() => add(t)}
              className="group cursor-pointer border-b py-2"
              style={{ borderColor: 'color-mix(in srgb, var(--line) 60%, transparent)' }}
            >
              <div className="flex items-baseline gap-3">
                <span className="min-w-0 flex-1 truncate text-fg/80">
                  {t.title}
                  <span className="text-muted"> — {t.artist}</span>
                </span>
                {added === t.videoId ? (
                  <span className="shrink-0 text-xs text-seal">已加入</span>
                ) : (
                  <span className="shrink-0 text-xs tabular-nums text-faint">
                    {t.durationSec ? fmtTime(t.durationSec) : ''}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
